import React from "react";
import { SearchSuggestionViewModel } from "../../api/generated/models/SearchSuggestionViewModel";
import { Loader } from "../Loader";
import { TypesenseBusinessDocument } from "../../utils/typesenseBusinessSearch";
import { normalizeLogoUrl } from "../../helpers/searchSuggestions";

const CategoryChip: React.FC<{ id: string; name?: string }> = ({ id, name }) => (
  <span
    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-xs text-primary"
    title="Every active merchant in this category"
  >
    <span className="uppercase text-[10px] tracking-wide">Category</span>
    {name ?? (
      <span className="italic text-gray-400" title={id}>
        unknown
      </span>
    )}
  </span>
);

const MerchantChip: React.FC<{ id: string; business?: TypesenseBusinessDocument }> = ({
  id,
  business,
}) => {
  const logoUrl = normalizeLogoUrl(business?.logo_url);
  return (
    <span className="inline-flex items-center gap-1.5 pl-1 pr-2 py-0.5 rounded-full bg-gray-100 text-xs text-gray-700">
      {logoUrl ? (
        <img
          src={logoUrl}
          alt=""
          className="w-5 h-5 rounded-full object-cover border border-gray-200"
        />
      ) : (
        <span className="w-5 h-5 rounded-full bg-gray-300" />
      )}
      {business?.business_name ?? (
        <span className="italic text-gray-400" title={id}>
          not in search index
        </span>
      )}
    </span>
  );
};

const SearchSuggestionsList: React.FC<{
  suggestions: SearchSuggestionViewModel[] | null;
  businesses: Map<string, TypesenseBusinessDocument>;
  categoryNames: Map<string, string>;
  onEdit: (suggestion: SearchSuggestionViewModel) => void;
  onDelete: (suggestion: SearchSuggestionViewModel) => void;
  onTogglePublished: (suggestion: SearchSuggestionViewModel) => void;
}> = ({ suggestions, businesses, categoryNames, onEdit, onDelete, onTogglePublished }) => (
  <section className="mb-8">
    <h3 className="text-lg font-medium mb-1">Suggestions</h3>
    <p className="text-xs text-gray-400 mb-4">
      Only published suggestions are shown to users. A keyword can belong to one
      suggestion only.
    </p>

    {suggestions === null ? (
      <Loader className="w-8 h-8" />
    ) : suggestions.length === 0 ? (
      <div className="text-sm text-gray-500">
        No suggestions yet. Map a zero-result query above to create one.
      </div>
    ) : (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              <th className="text-left font-medium px-4 py-3">Keywords</th>
              <th className="text-left font-medium px-4 py-3">Merchants & categories</th>
              <th className="text-left font-medium px-4 py-3">Status</th>
              <th className="text-right font-medium px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {suggestions.map((suggestion) => (
              <tr key={suggestion.id} className="border-t border-gray-100 align-top">
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {(suggestion.keywords ?? []).map((keyword) => (
                      <span
                        key={keyword}
                        className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs"
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                  {suggestion.note && (
                    <p className="text-xs text-gray-400 mt-1">{suggestion.note}</p>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {(suggestion.businessIds ?? []).map((id) => (
                      <MerchantChip key={id} id={id} business={businesses.get(id)} />
                    ))}
                    {(suggestion.categoryIds ?? []).map((id) => (
                      <CategoryChip key={id} id={id} name={categoryNames.get(id)} />
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <button
                    type="button"
                    onClick={() => onTogglePublished(suggestion)}
                    className={`text-xs px-2 py-1 rounded-full ${
                      suggestion.isPublished
                        ? "bg-green-100 text-green-700 hover:bg-green-200"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                    title={suggestion.isPublished ? "Click to unpublish" : "Click to publish"}
                  >
                    {suggestion.isPublished ? "Published" : "Draft"}
                  </button>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button
                    type="button"
                    onClick={() => onEdit(suggestion)}
                    className="text-primary hover:underline text-sm mr-3"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(suggestion)}
                    className="text-red-500 hover:underline text-sm"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </section>
);

export default SearchSuggestionsList;
